import React, { useState, useEffect, memo } from 'react';
import axios from 'axios';
import AdminSidebarRaw from '../Components/AdminSidebar';
import { API_BASE_URL } from '../config';
import '../Styles/AdminUsers.css';

const AdminSidebar = memo(AdminSidebarRaw);

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingUser, setEditingUser] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [updateSuccess, setUpdateSuccess] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const usersPerPage = 10;

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const fetchUsers = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/admin/users`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setUsers(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = (userId) => {
        setDeleteConfirmId(userId);
    };

    const confirmDelete = async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            await axios.delete(`${API_BASE_URL}/admin/users/${deleteConfirmId}`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            setUsers(users.filter(user => user.id !== deleteConfirmId));
        } catch (err) {
            console.error('Error deleting user:', err);
        } finally {
            setDeleteConfirmId(null);
        }
    };

    const handleEditUser = (user) => {
        setEditingUser({ ...user });
        setIsEditModalOpen(true);
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        const adminToken = localStorage.getItem('adminToken');
        try {
            const response = await axios.put(`${API_BASE_URL}/admin/users/${editingUser.id}`, editingUser, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            // Merge existing user to prevent resetting created_at date
            setUsers(users.map(u => u.id === editingUser.id ? { ...u, ...response.data } : u));
            setUpdateSuccess(true);
            setTimeout(() => {
                setUpdateSuccess(false);
                setIsEditModalOpen(false);
                setEditingUser(null);
            }, 1500);
        } catch (err) {
            console.error('Error updating user:', err);
            alert('Failed to update user');
        }
    };

    const filteredUsers = users.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone_number?.includes(searchTerm) ||
        user.id?.toString().includes(searchTerm)
    );

    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    return (
        <div className="admin-users-container">
            <AdminSidebar />

            <main className="admin-users-main">


                <div className="au-search-wrapper">
                    <svg className="au-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="text"
                        className="au-search-input"
                        placeholder="Search by ID, name, email or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="admin-users-table-wrapper">
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>Loading users...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : filteredUsers.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>No users found.</div>
                    ) : (
                        <>
                        <table className="admin-users-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>User</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Location</th>
                                    <th>Address 1</th>
                                    <th>Address 2</th>
                                    <th>Street</th>
                                    <th>Building</th>
                                    <th>Floor</th>
                                    <th>Joined</th>
                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentUsers.map(user => (
                                    <tr key={user.id}>
                                        <td data-label="ID" style={{ fontWeight: 600, color: 'var(--au-text-sub)' }}>#{user.id}</td>
                                        <td data-label="User">
                                            <span style={{ fontWeight: 700 }}>{user.name}</span>
                                        </td>
                                        <td data-label="Email">{user.email}</td>
                                        <td data-label="Phone">{user.phone_number || 'N/A'}</td>
                                        <td data-label="Location">{user.city ? `${user.city}, ${user.country}` : 'N/A'}</td>
                                        <td data-label="Address 1">{user.address1 || 'N/A'}</td>
                                        <td data-label="Address 2">{user.address2 || 'N/A'}</td>
                                        <td data-label="Street">{user.street || 'N/A'}</td>
                                        <td data-label="Building">{user.building || 'N/A'}</td>
                                        <td data-label="Floor">{user.floor || 'N/A'}</td>
                                        <td data-label="Joined">{formatDate(user.created_at)}</td>
                                        <td data-label="Actions" style={{ textAlign: 'right' }}>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                <button
                                                    className="admin-action-btn edit"
                                                    onClick={() => handleEditUser(user)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="admin-action-btn delete"
                                                    onClick={() => handleDeleteUser(user.id)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderTop: '1px solid var(--au-border)' }}>
                                <button 
                                    disabled={currentPage === 1} 
                                    onClick={() => setCurrentPage(prev => prev - 1)}
                                    className="admin-action-btn"
                                >
                                    Previous
                                </button>
                                <span style={{ fontWeight: 600, color: 'var(--au-text-sub)', fontSize: '0.9rem' }}>
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button 
                                    disabled={currentPage === totalPages} 
                                    onClick={() => setCurrentPage(prev => prev + 1)}
                                    className="admin-action-btn"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                        </>
                    )}
                </div>
            </main>

            {isEditModalOpen && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content">
                        <div className="admin-modal-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <h2>Edit User</h2>
                                {updateSuccess && <span className="au-success-badge">User updated successfully</span>}
                            </div>

                        </div>
                        <form onSubmit={handleUpdateUser} className="admin-modal-form">
                            <div className="form-group">
                                <label>Full Name</label>
                                <input
                                    type="text"
                                    value={editingUser.name || ''}
                                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Email Address</label>
                                    <input
                                        type="email"
                                        value={editingUser.email || ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Phone Number</label>
                                    <input
                                        type="text"
                                        value={editingUser.phone_number || ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, phone_number: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>City</label>
                                    <input
                                        type="text"
                                        value={editingUser.city || ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, city: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Country</label>
                                    <input
                                        type="text"
                                        value={editingUser.country || ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, country: e.target.value })}
                                    />
                                </div>
                            </div>
                             <div className="form-row">
                                <div className="form-group">
                                    <label>Address 1</label>
                                    <input
                                        type="text"
                                        value={editingUser.address1 || ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, address1: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Address 2</label>
                                    <input
                                        type="text"
                                        value={editingUser.address2 || ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, address2: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Street</label>
                                    <input
                                        type="text"
                                        value={editingUser.street || ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, street: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Building</label>
                                    <input
                                        type="text"
                                        value={editingUser.building || ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, building: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Floor</label>
                                    <input
                                        type="text"
                                        value={editingUser.floor || ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, floor: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="admin-modal-actions">
                                <button type="button" className="admin-action-btn cancel" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                                <button type="submit" className="admin-action-btn save">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {deleteConfirmId && (
                <div className="admin-modal-overlay">
                    <div className="au-confirm-dialog">
                        <div className="au-confirm-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14H6L5 6" />
                                <path d="M10 11v6M14 11v6" />
                                <path d="M9 6V4h6v2" />
                            </svg>
                        </div>
                        <h3 className="au-confirm-title">Delete User?</h3>
                        <p className="au-confirm-msg">Are you sure you want to delete this user? This action cannot be undone.</p>
                        <div className="au-confirm-actions">
                            <button className="admin-action-btn" onClick={() => setDeleteConfirmId(null)}>Cancel</button>
                            <button className="admin-action-btn delete" onClick={confirmDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsers;
